import React, { useState, useEffect } from 'react';
import { Save, RotateCcw, Loader2 } from 'lucide-react';

import Button from '@components/common/Button';
import { Checkbox, Select } from '@components/common';
import SettingsSectionHeader from '@components/features/data-management-settings/SettingsSectionHeader';
import { useReportDirty } from '@components/features/data-management-settings/useReportDirty';
import type { SettingsPaneProps } from '@components/features/data-management-settings/settingsNav';
import type { ConflictResolutionConfig } from '@app-types/import';
import { DEFAULT_CONFLICT_CONFIG } from '@app-types/import';
import type { User, ApiError } from '@app-types';
import { importService, userService } from '@services';
import { formatPersonName } from '@utils/stringUtils';
import { showToast } from '@utils/toast';
import styles from './ImportSettings.module.css';

const DUPLICATE_OPTIONS = [
  { value: 'update', label: 'Update existing record' },
  { value: 'skip', label: 'Skip row (keep existing)' },
  { value: 'create_new', label: 'Create new version' }
];

const SEALED_OPTIONS = [
  { value: 'skip_all', label: 'Skip all changes' },
  { value: 'update_metadata_only', label: 'Update metadata fields only' },
  { value: 'force_update', label: 'Force update (admin override)' }
];

const UNKNOWN_USER_OPTIONS = [
  { value: 'skip_user', label: 'Skip user (contract created without owner/manager)' },
  { value: 'skip_contract', label: 'Skip the entire contract' },
  { value: 'assign_default', label: 'Assign default contract owner' },
  { value: 'create_contact', label: 'Create new contact (roleless user)' }
];

const REFERENCE_DATA_OPTIONS = [
  { value: 'auto_add', label: 'Auto-add new values to reference list' },
  { value: 'reject', label: 'Reject row with unknown value' }
];

const TERMINATION_OPTIONS = [
  { value: 'expire_assessments', label: 'Auto-expire open assessments' },
  { value: 'keep_assessments', label: 'Keep assessments open' }
];

const CRITICAL_FIELDS = [
  { key: 'tier', label: 'Tier' },
  { key: 'category', label: 'Category' },
  { key: 'contract_type', label: 'Contract Type' },
  { key: 'supplier_number', label: 'Supplier' },
  { key: 'contract_scope', label: 'Contract Scope' },
  { key: 'start_date', label: 'Start Date' },
  { key: 'end_date', label: 'End Date' },
  { key: 'amount', label: 'Amount' }
];

const METADATA_FIELDS = [
  { key: 'description', label: 'Description' },
  { key: 'cost_center', label: 'Cost Center' },
  { key: 'parent_agreement_number', label: 'Parent Agreement Number' },
  { key: 'cms_contract_status', label: 'CMS Contract Status' },
  { key: 'contract_owner_email', label: 'Contract Owner Email' },
  { key: 'contract_manager_email', label: 'Contract Manager Email' },
  { key: 'additional_contract_data_1', label: 'Additional Data 1' },
  { key: 'additional_contract_data_2', label: 'Additional Data 2' },
  { key: 'additional_contract_data_3', label: 'Additional Data 3' }
];

const ImportSettings: React.FC<SettingsPaneProps> = ({ onDirtyChange }) => {
  const [config, setConfig] = useState<ConflictResolutionConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [managers, setManagers] = useState<User[]>([]);

  // Load config and users on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [savedConfig, contacts] = await Promise.all([
          importService.getConflictResolutionConfig(),
          userService.getUsers()
        ]);
        setConfig(savedConfig);
        setUsers(contacts);
        setManagers(contacts);
      } catch (error) {
        showToast.error('Failed to load settings');
        setConfig(DEFAULT_CONFLICT_CONFIG);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  useReportDirty(isDirty, onDirtyChange);

  const handleChange = (field: keyof ConflictResolutionConfig, value: string | string[] | number | null) => {
    setConfig((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        [field]: value
      };
    });
    setIsDirty(true);
  };

  const handleCriticalFieldToggle = (fieldKey: string) => {
    if (!config) return;

    const currentCritical = config.sealed_critical_fields;
    const currentMetadata = config.sealed_metadata_fields;

    if (currentCritical.includes(fieldKey)) {
      // Move to metadata
      setConfig((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          sealed_critical_fields: currentCritical.filter((f) => f !== fieldKey),
          sealed_metadata_fields: [...currentMetadata, fieldKey]
        };
      });
    } else {
      // Move to critical
      setConfig((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          sealed_critical_fields: [...currentCritical, fieldKey],
          sealed_metadata_fields: currentMetadata.filter((f) => f !== fieldKey)
        };
      });
    }
    setIsDirty(true);
  };

  const handleReset = () => {
    setConfig(DEFAULT_CONFLICT_CONFIG);
    setIsDirty(true);
  };

  const handleSave = async () => {
    if (!config) return;

    // Validate required fields when assign_default is selected
    if (config.unknown_user_action === 'assign_default') {
      if (!config.default_contract_owner_id || !config.default_contract_manager_id) {
        showToast.error('Both Contract Owner and Contract Manager must be selected when using "Assign default" option');
        return;
      }
    }

    try {
      setSaving(true);
      await importService.updateConflictResolutionConfig(config);
      setIsDirty(false);
      showToast.success('Settings saved successfully');
    } catch (error) {
      const apiError = error as ApiError;
      showToast.error(apiError?.response?.data?.error || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className={styles.importSettingsLoading} data-test-id="import-settings-loading">
        <Loader2 size={48} className="spin" />
        <p>Loading settings...</p>
      </div>
    );
  }

  // Show error state
  if (!config) {
    return <div className={styles.importSettingsError}>Failed to load settings</div>;
  }

  return (
    <div className={styles.importSettings} data-test-id="import-settings">
      <SettingsSectionHeader
        title="Import Behavior"
        description="Choose how imports resolve duplicates, sealed records, unknown users, and new reference data."
      />

      {/* Duplicate Contracts */}
      <div className={styles.importSettingsSection}>
        <h3 className={styles.importSettingsSectionTitle}>Duplicate Contracts</h3>
        <p className={styles.importSettingsSectionDescription}>
          When a contract with the same ContractNumber already exists in the system:
        </p>
        <Select
          label="Default Action"
          value={config.duplicate_contract_action}
          onChange={(value) => handleChange('duplicate_contract_action', value)}
          options={DUPLICATE_OPTIONS.map((opt) => ({
            value: opt.value,
            label: opt.label
          }))}
          className={styles.importSettingsFieldSelect}
          dataTestId="duplicate-contract-action"
        />
      </div>

      {/* Sealed Records */}
      <div className={styles.importSettingsSection}>
        <h3 className={styles.importSettingsSectionTitle}>Sealed Records (In Assessment)</h3>
        <p className={styles.importSettingsSectionDescription}>When a contract is currently undergoing assessment:</p>
        <Select
          label="Default Action"
          value={config.sealed_record_action}
          onChange={(value) => handleChange('sealed_record_action', value)}
          options={SEALED_OPTIONS.map((opt) => ({
            value: opt.value,
            label: opt.label
          }))}
          className={styles.importSettingsFieldSelect}
          dataTestId="sealed-record-action"
        />

        {config.sealed_record_action === 'update_metadata_only' && (
          <div className={styles.importSettingsFieldGroup}>
            <div className={styles.importSettingsFieldGroupTitle}>
              Critical Fields (cannot update during assessment):
            </div>
            <div className={styles.importSettingsCheckboxList}>
              {CRITICAL_FIELDS.map((field) => (
                <Checkbox
                  key={field.key}
                  label={field.label}
                  checked={config.sealed_critical_fields.includes(field.key)}
                  onChange={() => handleCriticalFieldToggle(field.key)}
                  wrapperClassName={styles.importSettingsCheckboxItem}
                />
              ))}
            </div>

            <div className={styles.importSettingsFieldGroupTitle} style={{ marginTop: '16px' }}>
              Metadata Fields (can update during assessment):
            </div>
            <div className={styles.importSettingsCheckboxList}>
              {METADATA_FIELDS.map((field) => (
                <Checkbox
                  key={field.key}
                  label={field.label}
                  checked={config.sealed_metadata_fields.includes(field.key)}
                  onChange={() => handleCriticalFieldToggle(field.key)}
                  wrapperClassName={styles.importSettingsCheckboxItem}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Unknown Users */}
      <div className={styles.importSettingsSection}>
        <h3 className={styles.importSettingsSectionTitle}>Unknown Users</h3>
        <p className={styles.importSettingsSectionDescription}>
          When ContractOwner or ContractManager email is not found in the system:
        </p>
        <Select
          label="Default Action"
          value={config.unknown_user_action}
          onChange={(value) => handleChange('unknown_user_action', value)}
          options={UNKNOWN_USER_OPTIONS.map((opt) => ({
            value: opt.value,
            label: opt.label
          }))}
          className={styles.importSettingsFieldSelect}
          dataTestId="unknown-user-action"
        />

        {config.unknown_user_action === 'assign_default' && (
          <div className={styles.importSettingsFieldGroup}>
            <div className={styles.importSettingsFieldGroupTitle}>Default User Assignment:</div>

            {/* Contract Owner Selector */}
            <Select
              label="Default Contract Owner"
              value={config.default_contract_owner_id ? String(config.default_contract_owner_id) : ''}
              onChange={(value) => handleChange('default_contract_owner_id', value ? Number.parseInt(value, 10) : null)}
              options={[
                { value: '', label: 'Select a default owner...' },
                ...users.map((user) => ({
                  value: String(user.id),
                  label: `${formatPersonName(user)} (${user.email})`
                }))
              ]}
              className={styles.importSettingsFieldSelect}
              dataTestId="default-contract-owner"
            />
            {!config.default_contract_owner_id && (
              <p style={{ fontSize: '13px', color: 'var(--warning-color)', marginTop: '8px' }}>
                ⚠️ Please select a default owner (required)
              </p>
            )}

            {/* Contract Manager Selector */}
            <Select
              label="Default Contract Manager"
              value={config.default_contract_manager_id ? String(config.default_contract_manager_id) : ''}
              onChange={(value) => handleChange('default_contract_manager_id', value ? Number.parseInt(value, 10) : null)}
              options={[
                { value: '', label: 'Select a default manager...' },
                ...managers.map((user) => ({
                  value: String(user.id),
                  label: `${formatPersonName(user)} (${user.email})`
                }))
              ]}
              className={styles.importSettingsFieldSelect}
              dataTestId="default-contract-manager"
            />
            {!config.default_contract_manager_id && (
              <p style={{ fontSize: '13px', color: 'var(--warning-color)', marginTop: '8px' }}>
                ⚠️ Please select a default manager (required)
              </p>
            )}
          </div>
        )}
      </div>

      {/* New Reference Data */}
      <div className={styles.importSettingsSection}>
        <h3 className={styles.importSettingsSectionTitle}>New Reference Data</h3>
        <p className={styles.importSettingsSectionDescription}>
          When ContractType or Category value is not in the reference list:
        </p>
        <Select
          label="Default Action"
          value={config.new_reference_data_action}
          onChange={(value) => handleChange('new_reference_data_action', value)}
          options={REFERENCE_DATA_OPTIONS.map((opt) => ({
            value: opt.value,
            label: opt.label
          }))}
          className={styles.importSettingsFieldSelect}
          dataTestId="new-reference-data-action"
        />
        <p style={{ fontSize: '13px', color: 'var(--warning-color)', marginTop: '8px' }}>
          ⚠️ Note: Tier and Contract Status values must always match existing values
        </p>
      </div>

      {/* Contract Termination */}
      <div className={styles.importSettingsSection}>
        <h3 className={styles.importSettingsSectionTitle}>Contract Termination</h3>
        <p className={styles.importSettingsSectionDescription}>
          When CMSContractStatus = "Terminated" for a contract with open assessments:
        </p>
        <Select
          label="Default Action"
          value={config.termination_action}
          onChange={(value) => handleChange('termination_action', value)}
          options={TERMINATION_OPTIONS.map((opt) => ({
            value: opt.value,
            label: opt.label
          }))}
          className={styles.importSettingsFieldSelect}
          dataTestId="termination-action"
        />
      </div>

      {/* Actions */}
      <div className={styles.importSettingsActions}>
        <Button variant="secondary" onClick={handleReset} disabled={saving}>
          <RotateCcw size={16} />
          Reset to Defaults
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={!isDirty || saving}>
          {saving ? <Loader2 size={16} className="spin" /> : <Save size={16} />}
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>
    </div>
  );
};

export default ImportSettings;
